import * as React from "react";
import { format, parseISO, isValid, startOfToday } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDateLocale } from "@/hooks/useDateLocale";
import { formatLocalizedDate } from "@/lib/localized-date";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DatePickerProps {
  /** ISO "yyyy-MM-dd" string or "" */
  value?: string;
  /** Emits ISO "yyyy-MM-dd" string on selection */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When true, all dates before today are greyed out and unselectable */
  disablePastDates?: boolean;
  /** When true, all dates after today are greyed out and unselectable (e.g. birthdays) */
  disableFutureDates?: boolean;
  id?: string;
  className?: string;
}

// ---------------------------------------------------------------------------
// Static data — not locale-dependent
// ---------------------------------------------------------------------------

const CURRENT_YEAR = new Date().getFullYear();
const YEARS: number[] = [];
for (let y = CURRENT_YEAR + 20; y >= CURRENT_YEAR - 100; y--) {
  YEARS.push(y);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseValue(value?: string): Date | undefined {
  if (!value) return undefined;
  const d = parseISO(value);
  return isValid(d) ? d : undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DatePicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  disablePastDates = false,
  disableFutureDates = false,
  id,
  className,
}: DatePickerProps) {
  const { t, i18n } = useTranslation();
  const locale = useDateLocale();

  // Locale-aware month names for the dropdown (standalone form, "LLLL" token)
  const monthNames = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        format(new Date(2024, i, 1), "LLLL", { locale }),
      ),
    [locale],
  );

  const [open, setOpen] = React.useState(false);
  const selectedDate = parseValue(value);

  // displayMonth controls which month the calendar grid shows.
  const [displayMonth, setDisplayMonth] = React.useState<Date>(
    selectedDate ?? new Date(),
  );

  // Sync displayMonth when value is updated externally (e.g. form reset).
  React.useEffect(() => {
    const d = parseValue(value);
    if (d) setDisplayMonth(d);
  }, [value]);

  function handleSelect(date: Date | undefined) {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
      setOpen(false);
    }
  }

  function handleTodayClick() {
    const today = startOfToday();
    onChange(format(today, "yyyy-MM-dd"));
    setDisplayMonth(today);
    setOpen(false);
  }

  const disabledMatcher = disablePastDates
    ? (d: Date) => d < startOfToday()
    : disableFutureDates
    ? (d: Date) => d > startOfToday()
    : undefined;

  const displayLabel = placeholder ?? t("leave.pickDate");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal bg-white dark:bg-card",
            !selectedDate && "text-muted-foreground",
            className,
          )}
        >
          {selectedDate
            ? formatLocalizedDate(value, i18n.resolvedLanguage || i18n.language)
            : displayLabel}
          <CalendarIcon className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        {/* Month / Year navigation dropdowns */}
        <div className="flex gap-2 p-3 pb-0">
          <Select
            value={String(displayMonth.getMonth())}
            onValueChange={(v) =>
              setDisplayMonth(
                new Date(displayMonth.getFullYear(), Number(v), 1),
              )
            }
          >
            <SelectTrigger className="h-8 flex-1 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthNames.map((name, i) => (
                <SelectItem key={i} value={String(i)}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={String(displayMonth.getFullYear())}
            onValueChange={(v) =>
              setDisplayMonth(
                new Date(Number(v), displayMonth.getMonth(), 1),
              )
            }
          >
            <SelectTrigger className="h-8 w-[5rem] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/*
          caption: "hidden"  — hides the native month label + prev/next arrows
          month: "space-y-0" — removes the 1rem top margin that space-y-4 still
                               applies to the table even when caption has the CSS
                               class "hidden" (Tailwind's space-y selector uses
                               :not([hidden]) which only matches the HTML attribute,
                               not a CSS class named "hidden")
        */}
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          disabled={disabledMatcher}
          locale={locale}
          initialFocus
          classNames={{
            caption: "hidden",
            month: "space-y-0",
          }}
        />

        {/* Today shortcut */}
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs text-primary hover:text-primary"
            onClick={handleTodayClick}
          >
            {t("dashboard.today")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
